import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { StoreActionApi, SetState, GetState, Action } from 'react-sweet-state';
import type { SNIPPET_CATEGORY_IDS } from '../../constants/snippetCategories.ts';
import type { SnippetSortOrder } from '../../constants/snippetSortOrder.ts';
import type { Snippet } from '../../types/snippet.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';
import type { CreateSnippetOptions } from './actionsHelper.ts';
import { createActions } from './createActions.ts';
import type { DeleteSnippetOptions } from './deleteActions.ts';
import { deleteActions } from './deleteActions.ts';
import type { FavoriteSnippetOptions } from './favoriteActions.ts';
import { favoriteActions } from './favoriteActions.ts';
import { fetchActions } from './fetchActions.ts';
import { insertCountActions } from './insertCountActions.ts';
import type { ClaimSnippetShareOptions, ShareSnippetOptions } from './shareActions.ts';
import { shareActions } from './shareActions.ts';
import { templateGalleryActions } from './templateGalleryActions.ts';
import type { UpdateSnippetOptions } from './updateActions.ts';
import { updateActions } from './updateActions.ts';

/**
 * Combined actions object for the snippets store.
 * Actions are grouped by concern for better organization and testability:
 * - fetchActions: Data fetching and cache management
 * - createActions: Snippet creation and duplication with UI integration (flags, quick insert)
 * - deleteActions: Snippet deletion (API + local state; pass editorAPI in options for quick insert removal)
 * - updateActions: Snippet update (API + local state; pass editorAPI in options for quick insert removal)
 * - favoriteActions: Snippet favorite (API + local state)
 * - shareActions: Share/unshare with error flag UI
 * - insertCountActions: Record snippet insert (API + local insertCount increment)
 * - templateGalleryActions: Template gallery Block tab state (selected snippet)
 */
export const actions: {
	setTemplateGallerySelectedSnippetId: (
		id: string | null,
	) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
	setTemplateGalleryCategoryFilter: (
		selectedCategoryId: SNIPPET_CATEGORY_IDS,
	) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
	setTemplateGallerySearchQuery: (
		searchQuery: string,
	) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
	setTemplateGallerySortOrder: (
		sortOrder: SnippetSortOrder,
	) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
	resetTemplateGalleryFilters: () => ({
		getState,
		setState,
	}: StoreActionApi<SnippetsState>) => void;
	recordSnippetInsert: (
		snippetId: string,
	) => ({ setState, getState }: StoreActionApi<SnippetsState>) => void;
	claimSnippetShare: (
		snippetId: string,
		shareId: string,
		options?: ClaimSnippetShareOptions,
	) => ({
		getState,
		setState,
	}: {
		setState: SetState<SnippetsState>;
		getState: GetState<SnippetsState>;
		dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
	}) => Promise<void>;
	shareSnippet: (
		snippetId: string,
		options?: ShareSnippetOptions,
	) => ({
		getState,
		setState,
	}: {
		setState: SetState<SnippetsState>;
		getState: GetState<SnippetsState>;
		dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
	}) => Promise<void>;
	unshareSnippet: (
		snippetId: string,
		options?: ShareSnippetOptions,
	) => ({
		getState,
		setState,
	}: {
		setState: SetState<SnippetsState>;
		getState: GetState<SnippetsState>;
		dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
	}) => Promise<void>;
	favoriteSnippet: (
		snippetId: string,
		options?: FavoriteSnippetOptions,
	) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
	unfavoriteSnippet: (
		snippetId: string,
		options?: FavoriteSnippetOptions,
	) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
	updateSnippet: (
		snippetId: string,
		name: string,
		body: ADFEntity,
		description?: string,
		options?: UpdateSnippetOptions,
	) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
	deleteSnippet: (
		snippetId: string,
		options?: DeleteSnippetOptions,
	) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<void>;
	createSnippet: (
		name: string,
		body: ADFEntity,
		description?: string,
		options?: CreateSnippetOptions,
	) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
	fetchSnippets: (
		locale?: string,
	) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<Snippet[]>;
	clearCache: () => ({ setState }: StoreActionApi<SnippetsState>) => void;
} = {
	...fetchActions,
	...createActions,
	...deleteActions,
	...updateActions,
	...favoriteActions,
	...shareActions,
	...insertCountActions,
	...templateGalleryActions,
};
